import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
  attributeIds,
  colRenderer,
} from "components/utils/ProductAttributesMapping";
import { ReactComponent as Pdf } from "assets/svg/pdf.svg";
import T from "i18n";
import ReactMarkdown from "react-markdown";
import GA from "components/utils/GA";
import Button from "./atoms/Button";
import ButtonWithIconContent from "./atoms/ButtonContent/ButtonWithIconContent";



const Wrapper = styled.div`
  .name {
    font-size: 2.5rem;
    ${({ theme }) => theme.mediaQueries.medium} {
      font-size: ${({ theme }) => theme.fontSize.desktopLarge};
    }
  }
  .banner {
    position: absolute;
    top: 30px;
    left: 0;
    width: 100%;
    height: 10px;
    background-size: cover;
    background-position: top left;
  }
  .logo {
    float: right;
  }
  .approvalLevel {
    font-weight:bold;

    .label {
      margin-right:5px;
    }
  }
  .stats {
    @media print {
      display:block; 
      div {
        display:inline-block;
        width:45%;        
      }
    }
    display: grid;
    grid-template-columns: 1fr 1fr;
    color: ${({ theme }) => theme.palette.primary};
    font-weight: bold;
    margin-bottom: ${({ theme }) => theme.spacer * 2}px;

    .label {
      width: 100%;
      padding: 6px 0;
    }
    .value {
      text-align: center;
      white-space: nowrap;
      svg {
        height: 1.1rem;
        margin: 0 1px;
      }
    }
  }

  .CTAs {
    @media print {
      display:block; 
    }
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: ${({ theme }) => theme.spacer}px;
    align-self: start;

    svg {
      font-size: 2.4rem;
      margin: -${({ theme }) => theme.spacer}px 0;
      path {
        fill: ${({ theme }) => theme.palette.secondary};
      }
    }
  }
`;

const ProductContentWrapper = styled.div`
  .stats {
    > * {
      padding: 0 ${({ theme }) => theme.spacer * 2}px;
      border-right: 1px solid ${({ theme }) => theme.palette.darker};      
      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        padding-right: 0;
        border: none;
      }
    }
  }

  ${({ theme }) => theme.mediaQueries.large} {

      display:grid;
      grid-template-columns: 1fr 1fr;

      > * {
      padding: 0 ${({ theme }) => theme.spacer * 2}px;
      /* height: 100%; */
      border-right: 1px solid ${({ theme }) => theme.palette.darker};

      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        padding-right: 0;
        border: none;
      }
    }

    
  }

  @media print {
    display: block;

    .stats {
      margin: 0;
    }
    .CTAs {
      display: none;
    }
  }
`;

const ProductCard = ({ className, product }) => {
  const classes = ["ProductCard"];
  if (className) classes.push(className);

  const leftAttributes = attributeIds.filter(
    (attr, index) => attr !== "name" && index < 4
  );
  const rightAttributes = attributeIds.filter((attr, index) => index > 3);

  function onViewDetails() {
    GA.event({
      category: "result_page",
      action: "view_product_details",
      label: product.product_page_link,
    });
    window.open(product.product_page_link, "_blank");
  }

  function onDownloadPerformancePdf() {
    GA.event({
      category: "result_page",
      action: "download_performance_pdf",
      label: product.performance_profile_pdf_link,
    });
    window.open(product.performance_profile_pdf_link, "_blank");
  }

  function onDownloadStoriePdf() {
    GA.event({
      category: "result_page",
      action: "download_success_stories_pdf",
      label: product.pop_pdf_link,
    });
    window.open(product.pop_pdf_link, "_blank");
  }
  
  const attributeLabelsMap = {
    name: T({path: "productTable.productName"}),
    engine_cleanliness: T({path: "productTable.engineCleanliness"}),
    oxidation_nitration: T({path: "productTable.oxidationResistance"}),
    performance_odi: T({path: "productTable.oilDrainInterval"}),
    sae_grade: T({path: "productTable.saeGrade"}),
    sulfated_ash_percentage: T({path: "productTable.sulphatedAsh"}),
    tbn: T({path: "productTable.totalBaseNumber"}),
    wear_protection: T({path: "productTable.wearProtection"}),
  };

  return (
    <Wrapper className={classes.join(" ")}>
      {(product.banner != null) && (
        <div className="banner" style={{ backgroundImage: `url(${product.banner})` }} />
      )}
      {(product.logo != null) && (
        <img className="logo" src={product.logo} alt="" />
      )}
      <h2 className="name">{product.name}</h2>
      <p className="approvalLevel">
        <span className="label">
          {T({ path: "resultsPage.approvalType" })}
          :
        </span>
        {product.approval_level}
      </p>
      <p className="description">
        <ReactMarkdown allowDangerousHtml>{product.description}</ReactMarkdown>
      </p>

      <ProductContentWrapper>
        <div className="stats">
          <div>
            <table>
              <tbody>
                {leftAttributes.map((attr) => (
                  <tr key={attr}>
                    <td className="label">{attributeLabelsMap[attr]}</td>
                    <td className="value">
                      {colRenderer(attr, product[attr])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <table>
              <tbody>
                {rightAttributes.map((attr) => (
                  <tr key={attr}>
                    <td className="label">{attributeLabelsMap[attr]}</td>
                    <td className="value">
                      {colRenderer(attr, product[attr])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="CTAs">
          {(product.product_page_link !== "") && (
            <Button className="ellipsis" onClick={onViewDetails}>
                {T({ path: "button.viewProductDetails" })}
            </Button>
          )}       

          {(product.pop_pdf_link !== "") && (
            <Button variation="secondary" className="ellipsis" onClick={onDownloadStoriePdf}>
              <ButtonWithIconContent icon={<Pdf />}>
                {T({ path: "button.alt_downloadSuccessStories" })}
              </ButtonWithIconContent>
            </Button>
          )}          
          
          {(product.performance_profile_pdf_link !== "") && (
            <Button className="ellipsis" variation="secondary" onClick={onDownloadPerformancePdf}>
              <ButtonWithIconContent icon={<Pdf />}>
                {T({ path: "button.alt_downloadPerformanceProfile" })}
              </ButtonWithIconContent>
            </Button>
          )}
          
        </div>
      </ProductContentWrapper>
    </Wrapper>
  );
};

ProductCard.displayName = "ProductCard";

ProductCard.propTypes = {
  className: PropTypes.string,
  product: PropTypes.shape({
    banner: PropTypes.string,
    name: PropTypes.string,
    logo: PropTypes.string,
    product_page_link: PropTypes.string,
    performance_profile_pdf_link: PropTypes.string,
    pop_pdf_link: PropTypes.string,
    approval_level: PropTypes.string,
    description: PropTypes.string,
    stats: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.number,
      })
    ),
  }).isRequired,
};

ProductCard.defaultProps = {
  className: null,
};

export default ProductCard;
